import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { Collapse } from 'src/ui/components/collapse/sports/04/Collapse';
import { Collapse as CollapseUnified } from 'src/ui/components/collapse/sports/05/Collapse';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { EventRowMetaTime } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx.style';
import { MarketHeaderTimeCaption } from 'src/domains/sportsbook/webview/components/eventMarkets/marketGroup/MarketGroups.style';
import { getSimpleDateOutrights } from 'src/utils/date';
import { useAppStateContext } from 'src/appState/AppState';
import { EachWayDetails, EachWayTermsType } from './EachWayDetails';

interface MarketHeaderPropsType {
    name: string;
    isOpen: boolean;
    onClick: () => void;
    marketId: MarketId;
    event?: EventModel | null;
    eachWayTerms?: EachWayTermsType;
    keepVisibleWhileLoading?: boolean;
}

export const MarketHeader = observer('MarketHeader', (props: MarketHeaderPropsType): JSX.Element | null => {
    const { isOpen, marketId, name, onClick, event = null, eachWayTerms, keepVisibleWhileLoading = false } = props;
    const { languagesState, unifiedComponentConfig } = useAppStateContext();
    const { unifiedSportEventsTable } = unifiedComponentConfig;

    const eventModel = marketId.getEventId().getEventModel();
    if (eventModel === null) {
        return null;
    }

    const marketModel = marketId.getModel();

    if (keepVisibleWhileLoading === false) {
        if (marketModel === null) {
            return null;
        }

        if (unifiedSportEventsTable && marketModel.selections.length === 0) {
            return null;
        }
    }

    const getNameToDisplay = () => {
        let nameToDisplay = languagesState.getMarketTranslationName(name, eventModel.participants ?? null);

        if (!unifiedSportEventsTable) {
            return name === 'First Goalscorer'
                ? languagesState.getTranslation('_market.player to score', 'Player To Score')
                : nameToDisplay;
        }

        switch (name) {
            case 'First Goalscorer':
                if (marketModel?.templateId.includes('touchdown')) {
                    nameToDisplay = languagesState.getTranslation('_market.touchdown scorer', 'Touchdown Scorer');
                    break;
                }

                nameToDisplay = languagesState.getTranslation('_market.player to score', 'Player To Score');
                break;
            case 'Total goals (over/under ${line})':
                nameToDisplay = languagesState.getTranslation('_market.total goals', 'Total Goals');
                break;
            case '1st half goals (over/under ${line})':
                nameToDisplay = languagesState.getTranslation('_market.1st half goals', '1st Half Goals over/under');
                break;
        }

        const [, participant] = nameToDisplay.match(/total (.+) goals under\/over/i) ?? [];
        const matchWinnerGoals = nameToDisplay.match(/match winner.+\d.\d goals$/i);
        const [, half] = nameToDisplay.match(/exact (\d[a-z]{2}) half corners/i) ?? [];

        if (nameToDisplay.includes('Match corners')) {
            nameToDisplay = nameToDisplay.replace(/(corners).+/i, '$1');
        }

        if (matchWinnerGoals) {
            nameToDisplay = languagesState.getTranslation('_market.match winner goals', 'Match Winner Total Goals');
        }
        if (participant) {
            nameToDisplay = `Total Goals ${participant} over/under`;
        }
        if (half) {
            nameToDisplay = `Exact ${half} Half Corners over/under`;
        }

        return nameToDisplay;
    };
    if (name.includes('scorer')) {
    }
    const nameToDisplay = getNameToDisplay();
    const CollapseComponent = unifiedSportEventsTable ? CollapseUnified : Collapse;

    const showEachWayDetails = eachWayTerms !== undefined;
    const isOutrights = event !== null && event.template === 'outrights';

    const showDetails = showEachWayDetails || isOutrights;

    return (
        <>
            <CollapseComponent
                dataTest='collapse-market'
                dataTestMarketName={nameToDisplay}
                handleToggle={onClick}
                isOpen={isOpen}
                title={nameToDisplay}
            >
                {!unifiedSportEventsTable && eachWayTerms !== undefined ? (
                    <EachWayDetails terms={eachWayTerms} />
                ) : null}
            </CollapseComponent>
            {unifiedSportEventsTable && showDetails && isOpen ? (
                <MarketHeaderTimeCaption>
                    {showEachWayDetails ? <EachWayDetails terms={eachWayTerms} /> : null}
                    {isOutrights ? (
                        <EventRowMetaTime>
                            Closes on {getSimpleDateOutrights(event.timeSettingsStartTime, languagesState)}
                        </EventRowMetaTime>
                    ) : null}
                </MarketHeaderTimeCaption>
            ) : null}
        </>
    );
});
